import React, { useEffect, useState } from "react";
import { lawyerApi } from "@lawyer/services/lawyerApi";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, CalendarDays, Info, Zap, Lightbulb } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { nextLabel } from "@lawyer/components/mobile/MobileStatusCard";
import { fmtSlotTime, fmtIstDate } from "@lawyer/components/mobile/statusUtils";

// Merge the day's free 30-min cells into contiguous windows ("10:00 AM – 1:00 PM").
function windowsFor(slots) {
  const out = [];
  for (const s of slots) {
    const last = out[out.length - 1];
    if (last && last.end === s.time) last.end = s.end_time; else out.push({ start: s.time, end: s.end_time });
  }
  return out.map((w) => `${fmtSlotTime(w.start)} – ${fmtSlotTime(w.end)}`);
}

// `initialSlots` (free cells already in the dashboard payload) skips the /slots request.
function useDayWindows(lawyerId, date, initialSlots) {
  const [wins, setWins] = useState(null);
  const hasInitial = Array.isArray(initialSlots);
  useEffect(() => {
    if (hasInitial || !lawyerId || !date) { setWins(null); return undefined; }
    let stop = false;
    lawyerApi.get(`/lawyers/${lawyerId}/slots`, { params: { date } })
      .then((r) => { if (!stop) setWins(windowsFor(r.data?.slots || [])); }).catch(() => { if (!stop) setWins(null); });
    return () => { stop = true; };
  }, [lawyerId, date, hasInitial]);
  return hasInitial ? windowsFor(initialSlots) : wins;
}

function dayLabel(date) {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const d = fmtIstDate(`${date}T00:00:00+05:30`);
  return date === today ? `Today, ${d}` : date === tomorrow ? `Tomorrow, ${d}` : d;
}

// Phone-only secondary cards under the status card / today's list. Pure presentation over existing data.

export function NextAvailabilityCard({ next, lawyerId }) {
  const wins = useDayWindows(lawyerId, next?.date, next?.slots);
  return (
    <section data-testid="m-next-availability">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Next Availability</h2>
        <Link to={`${LAWYER_BASE}/availability`} className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1">See calendar <ArrowRight size={12} /></Link>
      </div>
      <div className="rounded-2xl bg-blue-50/70 border border-blue-100 p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-white text-blue-700 grid place-items-center shrink-0"><CalendarDays size={18} /></div>
        <div className="flex-1 min-w-0">
          {next ? (
            <>
              <p className="text-sm font-extrabold text-[#0A2540]" data-testid="m-next-availability-value">{dayLabel(next.date)}</p>
              {wins && wins.length > 0
                ? wins.slice(0, 3).map((w) => <p key={w} className="text-sm text-slate-700">{w}</p>)
                : <p className="text-sm text-slate-700">{fmtSlotTime(next.time)} onwards</p>}
            </>
          ) : (
            <p className="text-sm font-extrabold text-[#0A2540]" data-testid="m-next-availability-value">{nextLabel(next)}</p>
          )}
          <Link to={`${LAWYER_BASE}/availability`} className="mt-3 inline-flex h-10 px-4 items-center gap-1 rounded-xl bg-white border border-slate-200 text-sm font-bold text-[#0A2540]" data-testid="m-next-availability-manage">
            Manage Availability <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

const INFO_TONE = {
  emerald: { wrap: "bg-emerald-50 border-emerald-100", icon: "text-emerald-600", Icon: Zap },
  blue: { wrap: "bg-blue-50/70 border-blue-100", icon: "text-blue-600", Icon: Info },
  amber: { wrap: "bg-[#FBF4DF] border-[#F1E3B5]", icon: "text-[#b8860b]", Icon: Lightbulb },
};

export function InfoCard({ tone = "blue", title, body, testid }) {
  const t = INFO_TONE[tone];
  return (
    <div className={`rounded-2xl border p-4 flex items-start gap-3 ${t.wrap}`} data-testid={testid}>
      <t.Icon size={18} className={`${t.icon} shrink-0 mt-0.5`} />
      <div>
        <p className="text-sm font-bold text-[#0A2540]">{title}</p>
        <p className="mt-0.5 text-xs text-slate-600">{body}</p>
      </div>
    </div>
  );
}

export function NextConsultationCard({ consult }) {
  if (!consult) return null;
  return (
    <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid="m-next-consultation">
      <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 grid place-items-center shrink-0"><CalendarDays size={18} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0A2540]">Next Consultation</p>
        <p className="text-xs text-slate-600 truncate">{fmtSlotTime(consult.slot_time)} · {consult.user_name || "Client"}</p>
      </div>
      <ChevronRight size={16} className="text-slate-300" />
    </Link>
  );
}

export function RestOfTodayCard({ count }) {
  if (!count) return null;
  return (
    <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid="m-rest-of-today">
      <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 grid place-items-center shrink-0"><CalendarDays size={18} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0A2540]">Rest of Today</p>
        <p className="text-xs text-slate-600">{count} more consultation{count === 1 ? "" : "s"} scheduled</p>
      </div>
      <ChevronRight size={16} className="text-slate-300" />
    </Link>
  );
}
